// Decides WHEN the in-product feedback prompt opens.
//
// The server has already decided *whether* this member is due (one boolean meta). Everything here is
// about picking a moment that does not feel like an ambush — which is most of what separates a
// prompt people answer from one people resent.
//
// Every guard is re-evaluated at fire time rather than at arm time. That is the whole reason a
// single timer is enough in a soft-navigation app: the member may be on a completely different page
// 45 seconds after bootstrap ran, and what matters is where they are when we would interrupt them.
//
// Design: designs/01-now/2026-08-01-in-product-feedback-prompt-design.md §D6.

import {feedbackPromptState} from "@/main/feedback/feedbackPromptState";
import {didOnboardingWelcomeOpenThisLoad} from "@/main/onboarding/OnboardingWelcomeRuntime";
import {openFeedbackPrompt, preloadFeedbackPrompt} from "@/main/react/feedback/openFeedbackPromptLazy";
import {LocalizationUtil} from "@/main/util/LocalizationUtil";
import logger from "@/lib/logger";

/**
 * How long the member has to have been around before we interrupt them.
 *
 * Long enough that they came to do something and did it; short enough that they are still here.
 * Exported (and overridable) because a browser test must not spend 45 real seconds proving that a
 * timer works.
 */
export const FEEDBACK_PROMPT_DELAY_MS = 45_000;

/**
 * Route prefixes where we never interrupt. The composer is the important one: asking someone for
 * feedback mid-draft is the single worst moment available, and it is a route that already carries an
 * unsaved-changes guard precisely because losing work here is expensive.
 */
const EXCLUDED_PATH_PREFIXES = ["/create", "/meme"];

let armedThisLoad = false;
let timerId: ReturnType<typeof setTimeout> | null = null;
let delayMsOverride: number | null = null;

function delayMs(): number {
    return delayMsOverride ?? FEEDBACK_PROMPT_DELAY_MS;
}

function isOnExcludedRoute(): boolean {
    if (typeof window === "undefined") {
        return true;
    }
    const path = window.location.pathname;
    return EXCLUDED_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

/**
 * Whether something else already owns the member's attention.
 *
 * Queried from the DOM rather than from a store because there is no single registry of open dialogs —
 * Radix marks its open content `[data-state="open"]`, and every dialog in the app (SimpleDialog, the
 * account-access dialog, the share sheet) goes through it. A missed detection would stack a second
 * layer over the first, which is exactly the "spammy" failure this feature cannot afford on the one
 * impression it gets.
 */
function anotherDialogIsOpen(): boolean {
    if (typeof document === "undefined") {
        return true;
    }
    return document.querySelector('[role="dialog"][data-state="open"]') !== null
        || document.querySelector('[role="alertdialog"][data-state="open"]') !== null;
}

function shouldOpenNow(): boolean {
    return feedbackPromptState.isEligible()
        && !didOnboardingWelcomeOpenThisLoad()
        && !isOnExcludedRoute()
        && !anotherDialogIsOpen();
}

function clearTimer(): void {
    if (timerId !== null) {
        clearTimeout(timerId);
        timerId = null;
    }
}

function fire(): void {
    timerId = null;

    // A prompt that opens in a background tab is spent for nothing — the member never sees it, and
    // the impression is counted against them. Wait for them to come back instead of dropping it.
    if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        const onVisible = () => {
            if (document.visibilityState === "visible") {
                document.removeEventListener("visibilitychange", onVisible);
                fire();
            }
        };
        document.addEventListener("visibilitychange", onVisible);
        return;
    }

    if (!shouldOpenNow()) {
        return;
    }
    openFeedbackPrompt(LocalizationUtil.get);
}

/**
 * Arm the feedback prompt for this page load. Safe to call on every load: it no-ops for anonymous
 * visitors, for members the server has not marked due, and for repeat calls within one load. Never
 * throws into the bootstrap path.
 */
export function maybeScheduleFeedbackPrompt(): void {
    if (armedThisLoad) {
        return;
    }
    armedThisLoad = true;

    try {
        // Cheap pre-check so an ineligible member costs nothing at all — no timer, no listener. The
        // real decision still happens at fire time, because the member may have handled the prompt
        // in another tab in the meantime.
        if (!feedbackPromptState.isEligible() || didOnboardingWelcomeOpenThisLoad()) {
            return;
        }

        timerId = setTimeout(fire, delayMs());
        // Fetch the dialog's chunk now, while the page is still booting, so that a stale-chunk reload
        // (the one disruptive failure path of the chunk helper) can never happen mid-read at fire time.
        // FI-719: the dialog left the eager graph; see openFeedbackPromptLazy.ts.
        preloadFeedbackPrompt();

        if (typeof window !== "undefined") {
            window.addEventListener("pagehide", clearTimer, {once: true});
        }
    } catch (error) {
        logger.error("[feedback] failed to schedule the feedback prompt", error);
    }
}

/** Test-only: shorten the dwell delay so a spec does not have to wait 45 real seconds. */
export function setFeedbackPromptDelayForTests(ms: number | null): void {
    delayMsOverride = ms;
}

/** Test-only: clear the within-load guard and any pending timer. */
export function resetFeedbackPromptForTests(): void {
    clearTimer();
    armedThisLoad = false;
    delayMsOverride = null;
}
